import {
  Add as AddIcon,
  Star as AdvancedIcon,
  School as BeginnerIcon,
  Category as CategoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  TrendingUp as IntermediateIcon,
  Psychology as PsychologyIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePrograms } from "../../hooks/useRedux";
import { Program } from "../../store/slices/types";

const Programs: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const { programs, loading, error, deleteProgram, pagination } = usePrograms(
    paginationModel.page + 1,
    paginationModel.pageSize
  );
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Helper function to get category name from backend data
  const getCategoryName = (program: Program) => {
    // Use categoryDetails from backend (populated by backend)
    if (program.categoryDetails?.name) {
      return program.categoryDetails.name;
    }
    // Fallback to category field if categoryDetails not available
    return program.category || "Unknown";
  };

  // Helper function to get category ID from backend data
  const getCategoryId = (program: Program) => {
    // Use categoryDetails from backend
    if (program.categoryDetails?._id || program.categoryDetails?.id) {
      return program.categoryDetails._id || program.categoryDetails.id;
    }
    // Fallback to categoryId field
    return program.categoryId;
  };

  // Filter programs based on active filter (using backend category data)
  const filteredPrograms = activeFilter
    ? programs.filter((program) => {
        const categoryId = getCategoryId(program);
        const categoryName = getCategoryName(program);
        return (
          categoryName === activeFilter ||
          categoryId === activeFilter ||
          (program.categoryDetails?._id || program.categoryDetails?.id) ===
            activeFilter
        );
      })
    : programs;

  // Calculate statistics
  const totalPrograms = programs.length;
  const beginnerPrograms = programs.filter(
    (p) => p.difficulty === "beginner"
  ).length;
  const intermediatePrograms = programs.filter(
    (p) => p.difficulty === "intermediate"
  ).length;
  const advancedPrograms = programs.filter(
    (p) => p.difficulty === "advanced"
  ).length;

  const handleFilterClick = (category: string | null) => {
    setActiveFilter(category);
  };

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const getCategoryIcon = () => {
    // Use default category icon
    return <CategoryIcon />;
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return <BeginnerIcon />;
      case "intermediate":
        return <IntermediateIcon />;
      case "advanced":
        return <AdvancedIcon />;
      default:
        return <BeginnerIcon />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
        return "error";
      default:
        return "default";
    }
  };

  const getCategoryColor = () => {
    // Use default color since backend doesn't provide category color
    return "#667eea";
  };

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "category",
      headerName: "",
      flex: 0.1,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#667eea",
              color: "white",
            }}
          >
            {getCategoryIcon()}
          </Avatar>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2' fontWeight='600'>
            {params.value}
          </Typography>
          <Typography variant='caption' color='textSecondary' noWrap>
            {params.row.shortDescription?.substring(0, 50)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={`${params.value} days`}
          size='small'
          color='primary'
          variant='outlined'
        />
      ),
    },
    {
      field: "difficulty",
      headerName: "Difficulty",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => (
        <Chip
          icon={getDifficultyIcon(params.value)}
          label={params.value}
          size='small'
          color={getDifficultyColor(params.value) as any}
          variant='filled'
        />
      ),
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => {
        const categoryName = getCategoryName(params.row);
        return (
          <Chip
            label={categoryName}
            size='small'
            sx={{
              backgroundColor: "#667eea",
              color: "white",
              "&:hover": {
                backgroundColor: "#667eea",
                opacity: 0.8,
              },
            }}
          />
        );
      },
    },
    {
      field: "totalEnrollments",
      headerName: "Enrollments",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant='body2' fontWeight='600'>
            {params.value ? params.value.toFixed(1) : "N/A"}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            ⭐
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Program'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Program'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/programs/add");
  };

  const handleEdit = (program: Program) => {
    const id = program._id || program.id;
    if (id) {
      navigate(`/programs/edit/${id}`);
    }
  };

  const handleDelete = async (id: string) => {
    const program = programs.find((p) => p.id === id);
    const programTitle = program?.title || "Program";

    if (
      window.confirm(
        `Are you sure you want to delete "${programTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteProgram(id);
        toast.success("program is deleted");
      } catch (error) {
        console.error("Failed to delete program:", error);
        toast.error(
          `❌ Failed to delete program: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Program Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Create and manage wellness programs
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                },
              }}
            >
              Create Program
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  activeFilter === null
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: activeFilter === null ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === null
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick(null)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {totalPrograms}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Programs
                    </Typography>
                  </Box>
                  <PsychologyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "beginner" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "beginner"
                    ? "0 8px 25px rgba(76, 175, 80, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "beginner"
                    ? "2px solid #4caf50"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("beginner")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {beginnerPrograms}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Beginner
                    </Typography>
                  </Box>
                  <BeginnerIcon sx={{ fontSize: 40, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "intermediate" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "intermediate"
                    ? "0 8px 25px rgba(255, 152, 0, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "intermediate"
                    ? "2px solid #ff9800"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(255, 152, 0, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("intermediate")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {intermediatePrograms}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Intermediate
                    </Typography>
                  </Box>
                  <IntermediateIcon
                    sx={{ fontSize: 40, color: "warning.main" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "advanced" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "advanced"
                    ? "0 8px 25px rgba(244, 67, 54, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "advanced"
                    ? "2px solid #f44336"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(244, 67, 54, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("advanced")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='error.main'
                    >
                      {advancedPrograms}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Advanced
                    </Typography>
                  </Box>
                  <AdvancedIcon sx={{ fontSize: 40, color: "error.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Showing {activeFilter} programs:
          </Typography>
          <Chip
            label={activeFilter}
            color='primary'
            size='small'
            onDelete={() => handleFilterClick(null)}
          />
        </Box>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredPrograms}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={pagination?.total || 0}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Programs;
